import React, { useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    CircularProgress,
    Alert,
    Typography,
    Box,
    Grid,
} from '@mui/material';
import { useTestResult } from '../../hooks/useTestResult';
import QuestionWiseResult from './QuestionWiseResult';

interface StudentResultModalProps {
    open: boolean;
    onClose: () => void;
    testId: string;
    mappingId: string;
    studentName?: string;
}

const StudentResultModal: React.FC<StudentResultModalProps> = ({
    open,
    onClose,
    testId,
    mappingId,
    studentName,
}) => {
    const { testResult, loading, error, refetch } = useTestResult(testId, mappingId, true);

    useEffect(() => {
        if (open) {
            refetch();
        }
    }, [open, refetch]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Student Result {studentName && `- ${studentName}`}</DialogTitle>
            <DialogContent dividers>
                {loading ? (
                    <Box display="flex" justifyContent="center" py={3}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Alert severity="error">{error}</Alert>
                ) : testResult ? (
                    <>
                        <Box sx={{ py: 2 }}>
                            <Grid container spacing={3}>
                                <Grid item xs={6} sm={3}>
                                    <Box
                                        sx={{
                                            p: 2,
                                            borderRadius: 2,
                                            bgcolor: '#f5f5f5',
                                            textAlign: 'center',
                                            height: '100%',
                                        }}
                                    >
                                        <Typography
                                            variant="caption"
                                            color="textSecondary"
                                            sx={{ fontWeight: 600 }}
                                        >
                                            Total Questions
                                        </Typography>
                                        <Typography
                                            variant="h5"
                                            sx={{ mt: 1, fontWeight: 'bold', color: '#424242' }}
                                        >
                                            {(testResult as any).totalQuestions || 0}
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Box
                                        sx={{
                                            p: 2,
                                            borderRadius: 2,
                                            bgcolor: '#e3f2fd',
                                            textAlign: 'center',
                                            height: '100%',
                                        }}
                                    >
                                        <Typography
                                            variant="caption"
                                            color="textSecondary"
                                            sx={{ fontWeight: 600 }}
                                        >
                                            Attempted
                                        </Typography>
                                        <Typography
                                            variant="h5"
                                            sx={{ mt: 1, fontWeight: 'bold', color: '#1976d2' }}
                                        >
                                            {(testResult as any).totalQuestionsAttempted || 0}
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Box
                                        sx={{
                                            p: 2,
                                            borderRadius: 2,
                                            bgcolor: '#e8f5e9',
                                            textAlign: 'center',
                                            height: '100%',
                                        }}
                                    >
                                        <Typography
                                            variant="caption"
                                            color="textSecondary"
                                            sx={{ fontWeight: 600 }}
                                        >
                                            Correct
                                        </Typography>
                                        <Typography
                                            variant="h5"
                                            sx={{ mt: 1, fontWeight: 'bold', color: '#2e7d32' }}
                                        >
                                            {(testResult as any).totalCorrectQuestions || 0}
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Box
                                        sx={{
                                            p: 2,
                                            borderRadius: 2,
                                            bgcolor: '#ffebee',
                                            textAlign: 'center',
                                            height: '100%',
                                        }}
                                    >
                                        <Typography
                                            variant="caption"
                                            color="textSecondary"
                                            sx={{ fontWeight: 600 }}
                                        >
                                            Wrong
                                        </Typography>
                                        <Typography
                                            variant="h5"
                                            sx={{ mt: 1, fontWeight: 'bold', color: '#c62828' }}
                                        >
                                            {(testResult as any).totalIncorrectQuestions || 0}
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Box
                                        sx={{
                                            p: 2,
                                            borderRadius: 2,
                                            bgcolor: '#fff3e0',
                                            textAlign: 'center',
                                            height: '100%',
                                        }}
                                    >
                                        <Typography
                                            variant="caption"
                                            color="textSecondary"
                                            sx={{ fontWeight: 600 }}
                                        >
                                            Marks
                                        </Typography>
                                        <Typography
                                            variant="h5"
                                            sx={{ mt: 1, fontWeight: 'bold', color: '#e65100' }}
                                        >
                                            {(testResult as any).marksObtained || 0}/
                                            {(testResult as any).totalMarks || 0}
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Box
                                        sx={{
                                            p: 2,
                                            borderRadius: 2,
                                            bgcolor: '#f3e5f5',
                                            textAlign: 'center',
                                            height: '100%',
                                        }}
                                    >
                                        <Typography
                                            variant="caption"
                                            color="textSecondary"
                                            sx={{ fontWeight: 600 }}
                                        >
                                            Accuracy
                                        </Typography>
                                        <Typography
                                            variant="h5"
                                            sx={{ mt: 1, fontWeight: 'bold', color: '#7b1fa2' }}
                                        >
                                            {(testResult as any).accuracy || 0}%
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Box
                                        sx={{
                                            p: 2,
                                            borderRadius: 2,
                                            bgcolor: '#e1f5fe',
                                            textAlign: 'center',
                                            height: '100%',
                                        }}
                                    >
                                        <Typography
                                            variant="caption"
                                            color="textSecondary"
                                            sx={{ fontWeight: 600 }}
                                        >
                                            Rank
                                        </Typography>
                                        <Typography
                                            variant="h5"
                                            sx={{ mt: 1, fontWeight: 'bold', color: '#0277bd' }}
                                        >
                                            {(testResult as any).rank || 0}
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Box
                                        sx={{
                                            p: 2,
                                            borderRadius: 2,
                                            bgcolor: '#fce4ec',
                                            textAlign: 'center',
                                            height: '100%',
                                        }}
                                    >
                                        <Typography
                                            variant="caption"
                                            color="textSecondary"
                                            sx={{ fontWeight: 600 }}
                                        >
                                            Time (min)
                                        </Typography>
                                        <Typography
                                            variant="h5"
                                            sx={{ mt: 1, fontWeight: 'bold', color: '#c2185b' }}
                                        >
                                            {(testResult as any).totalTimeInMinutes || 0}
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>
                        <QuestionWiseResult result={testResult} />
                    </>
                ) : (
                    <Alert severity="info">No result available</Alert>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant="contained">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default StudentResultModal;
